/**
 * Shared inbox-thread menu item source (ADR-0020).
 * Right-click and overflow `...` must call the same builders.
 */
import {
  Archive,
  Clock,
  Copy,
  Eye,
  EyeOff,
  type LucideIcon,
} from 'lucide-react'
import type { EntityContextMenuEntry } from '@/components/features/entity-context-menu'
import { ADD_TO_CURRENT_CHAT_LABEL, AddToCurrentChatIcon } from '@/lib/add-to-current-chat'
import { OpenDestinationIcons } from '@/lib/open-destination-icons'
import type { Status } from './types'

export const INBOX_SNOOZE_OPTIONS = [
  { label: 'In 1 hour', hint: 'Today ~5pm', value: '1h' },
  { label: 'Later today', hint: 'Today 7pm', value: '4h' },
  { label: 'Tomorrow morning', hint: 'Tomorrow 9am', value: '1d' },
  { label: 'This weekend', hint: 'Sat 9am', value: '3d' },
  { label: 'Next Monday', hint: 'Mon 9am', value: '7d' },
] as const

export type InboxThreadMenuSurface = 'list' | 'reader'

export type InboxThreadMenuHandlers = {
  onOpenInNewTab?: () => void
  onPreview?: () => void
  onSnooze: (label: string, hint: string) => void
  onArchive: () => void
  onMarkAsUnread?: () => void
  onMarkAsRead?: () => void
  onOpenInChat?: () => void
  onAddToCurrentChat?: () => void
  onCopyLink?: () => void
}

export type InboxThreadMenuInput = {
  status: Status
  linkedConversationId?: string | null
  /** list = index row (includes Preview). reader = preview / full thread (Open in new tab, no Preview). */
  surface?: InboxThreadMenuSurface
  handlers: InboxThreadMenuHandlers
}

export type InboxThreadMenuActionId =
  | 'open-new-tab'
  | 'preview'
  | 'snooze'
  | 'archive'
  | 'mark-unread'
  | 'mark-read'
  | 'open-in-chat'
  | 'add-to-chat'
  | 'copy-link'

export type InboxThreadMenuAction = {
  id: InboxThreadMenuActionId
  label: string
  icon: LucideIcon
  disabled?: boolean
  /** Present only for snooze - submenu / flattened options. */
  snoozeOptions?: readonly { label: string; hint: string; value: string }[]
  onSelect?: () => void
}

/**
 * Canonical ordered actions for an inbox thread (before shell-specific rendering).
 * Mute is intentionally omitted until shipped (no permanent stubs).
 */
export function getInboxThreadMenuActions(input: InboxThreadMenuInput): InboxThreadMenuAction[] {
  const surface = input.surface ?? 'list'
  const { handlers, status, linkedConversationId } = input
  const canOpenChat = Boolean(handlers.onOpenInChat || linkedConversationId)
  const actions: InboxThreadMenuAction[] = []

  if (handlers.onOpenInNewTab) {
    actions.push({
      id: 'open-new-tab',
      label: 'Open in new tab',
      icon: OpenDestinationIcons.openInNewTab,
      onSelect: handlers.onOpenInNewTab,
    })
  }
  if (surface === 'list' && handlers.onPreview) {
    actions.push({
      id: 'preview',
      label: 'Preview',
      icon: OpenDestinationIcons.preview,
      onSelect: handlers.onPreview,
    })
  }

  if (status !== 'archived') {
    actions.push({
      id: 'snooze',
      label: 'Snooze',
      icon: Clock,
      snoozeOptions: INBOX_SNOOZE_OPTIONS,
    })
  }

  actions.push({
    id: 'archive',
    label: 'Archive',
    icon: Archive,
    onSelect: handlers.onArchive,
  })

  if (status === 'unread') {
    if (handlers.onMarkAsRead) {
      actions.push({
        id: 'mark-read',
        label: 'Mark as read',
        icon: Eye,
        onSelect: handlers.onMarkAsRead,
      })
    }
  } else if (status !== 'archived' && handlers.onMarkAsUnread) {
    actions.push({
      id: 'mark-unread',
      label: 'Mark as unread',
      icon: EyeOff,
      onSelect: handlers.onMarkAsUnread,
    })
  }

  if (canOpenChat && handlers.onOpenInChat) {
    actions.push({
      id: 'open-in-chat',
      label: 'Open in chat',
      icon: OpenDestinationIcons.openInChat,
      onSelect: handlers.onOpenInChat,
    })
  }

  if (handlers.onAddToCurrentChat) {
    actions.push({
      id: 'add-to-chat',
      label: ADD_TO_CURRENT_CHAT_LABEL,
      icon: AddToCurrentChatIcon,
      onSelect: handlers.onAddToCurrentChat,
    })
  }

  if (handlers.onCopyLink) {
    actions.push({
      id: 'copy-link',
      label: 'Copy link',
      icon: Copy,
      onSelect: handlers.onCopyLink,
    })
  }

  return actions
}

function isChatGroupAction(id: InboxThreadMenuActionId): boolean {
  return id === 'open-in-chat' || id === 'add-to-chat' || id === 'copy-link'
}

/**
 * Same actions as overflow `...`, with snooze as a submenu (not flat root items).
 */
export function getInboxThreadContextMenuEntries(
  input: InboxThreadMenuInput,
): EntityContextMenuEntry[] {
  const actions = getInboxThreadMenuActions(input)
  const entries: EntityContextMenuEntry[] = []
  let needsSepBeforeTriage = false
  let inChatGroup = false

  for (const action of actions) {
    if (action.id === 'open-new-tab' || action.id === 'preview') {
      entries.push({
        label: action.label,
        icon: action.icon,
        onSelect: () => action.onSelect?.(),
        disabled: action.disabled,
      })
      needsSepBeforeTriage = true
      continue
    }

    if (needsSepBeforeTriage) {
      entries.push({ separator: true })
      needsSepBeforeTriage = false
    }

    if (action.id === 'snooze' && action.snoozeOptions) {
      entries.push({
        label: action.label,
        icon: action.icon,
        children: action.snoozeOptions.map((opt) => ({
          label: opt.label,
          hint: opt.hint,
          icon: Clock,
          onSelect: () => input.handlers.onSnooze(opt.label, opt.hint),
        })),
      })
      continue
    }

    if (isChatGroupAction(action.id) && !inChatGroup) {
      const last = entries[entries.length - 1]
      if (last && !('separator' in last)) {
        entries.push({ separator: true })
      }
      inChatGroup = true
    }

    if (action.onSelect) {
      entries.push({
        label: action.label,
        icon: action.icon,
        onSelect: action.onSelect,
        disabled: action.disabled,
      })
    }
  }

  while (entries.length > 0) {
    const last = entries[entries.length - 1]
    if (last && 'separator' in last && last.separator) {
      entries.pop()
      continue
    }
    break
  }

  return entries
}
